import { RefObject, useEffect, useMemo, useRef } from "react";
import Moveable from "react-moveable";
import { createGlobalStyle } from "styled-components";
import { useTemplateEditorStore } from "../../../../store/useTemplateEditorStore";
import { collectSnapTargets } from "../../../../helpers/snapHelper";
import {
  clampAxis,
  getMaskCanvasGeometry,
  isMasked,
} from "../../../../helpers/maskHelper";
import { Mask } from "../../../../types";
import { useMoveableRegistry } from "../moveableRegistry";

type MoveableLayerProps = {
  previewWidth: number;
  previewHeight: number;
  /** The preview canvas element — used as Moveable's coordinate root. */
  rootRef: RefObject<HTMLElement>;
};

const GROUP_RESIZE_DIRECTIONS = ["nw", "ne", "sw", "se"];
// Snap free rotation to right angles (within threshold).
const ROTATION_SNAP_DEGREES = [0, 90, 180, 270, 360];
const ROTATION_SNAP_THRESHOLD = 7;
// Item snapping is always on (no UI toggle); threshold in px.
const SNAP_THRESHOLD = 5;

type Frame = { translate: [number, number]; rotate: number };

/**
 * Clamp a media item's translate so its (possibly rotated) crop mask stays
 * inside the canvas, while the hidden parts of the box may spill outside. The
 * box's top-left is the translate (the only free variable), so each axis is
 * clamped to keep the mask's rotated canvas extent within [0, canvas]; if the
 * mask is larger than the canvas on an axis it's centered instead (handled by
 * `clampAxis`). Geometry is shared with the editor via `getMaskCanvasGeometry`
 * — the rect's four-corner AABB is symmetric about the rotated mask center, so
 * `[-minX, canvasW - maxX]` reduces exactly to the ellipse's extent-based form.
 */
const clampMaskTranslate = (
  tx: number,
  ty: number,
  mask: Mask,
  boxW: number,
  boxH: number,
  rotationDeg: number,
  canvasW: number,
  canvasH: number
): [number, number] => {
  const { extentX, extentY, centerOffX, centerOffY } = getMaskCanvasGeometry(
    mask,
    boxW,
    boxH,
    rotationDeg
  );
  return [
    clampAxis(tx, extentX - centerOffX, canvasW - extentX - centerOffX),
    clampAxis(ty, extentY - centerOffY, canvasH - extentY - centerOffY),
  ];
};

/**
 * Single, container-level Moveable controller for the storyboard preview.
 * Targets the selected item element(s) (a group when >1) and translates
 * Moveable gestures into each item's relative-space commit via the registry.
 *
 * Transforms are managed through per-element frames seeded on gesture start
 * (`e.set`) so translate/rotate are preserved across separate gestures — e.g.
 * dragging a rotated item keeps its angle.
 */
export const MoveableLayer = ({
  previewWidth,
  previewHeight,
  rootRef,
}: MoveableLayerProps) => {
  const moveableRef = useRef<Moveable>(null);
  const registry = useMoveableRegistry();
  const framesRef = useRef(new Map<HTMLElement, Frame>());

  const { selectedItemIds, templateState, currentChapter, setIsInteracting } =
    useTemplateEditorStore((store) => ({
      selectedItemIds: store.selectedItemIds,
      templateState: store.templateState,
      currentChapter: store.currentChapter,
      setIsInteracting: store.setIsInteracting,
    }));

  // Resolve the selected DOM elements + a stable element→key map for groups.
  const { elements, elementKey } = useMemo(() => {
    const elements: HTMLElement[] = [];
    const elementKey = new Map<HTMLElement, string>();
    if (registry) {
      for (const key of selectedItemIds) {
        const el = registry.get(key)?.getElement();
        if (el) {
          elements.push(el);
          elementKey.set(el, key);
        }
      }
    }
    return { elements, elementKey };
    // version forces re-resolve when registrations change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registry, registry?.version, selectedItemIds]);

  // Keep Moveable's box aligned when items move via state (arrow keys, commits).
  useEffect(() => {
    moveableRef.current?.updateRect();
  }, [templateState, previewWidth, previewHeight, selectedItemIds]);

  // Let items ask the controls + bounding box to realign mid-gesture (e.g. a
  // crop drag that moves the item imperatively, without a state commit).
  useEffect(() => {
    registry?.setUpdateRect(() => moveableRef.current?.updateRect());
    return () => registry?.setUpdateRect(null);
  }, [registry]);

  const isGroup = elements.length > 1;
  const singleEl = !isGroup ? elements[0] : undefined;
  const singleEntry = singleEl
    ? registry?.get(elementKey.get(singleEl) ?? "")
    : undefined;
  const singleConfig = singleEntry?.getConfig();

  const entryFor = (el: HTMLElement | SVGElement | undefined) => {
    if (!(el instanceof HTMLElement)) return undefined;
    const key = elementKey.get(el);
    return key ? registry?.get(key) : undefined;
  };

  // ─── Per-element frame (seeded on gesture start, preserves rotate on drag) ──
  const seedFrame = (el: HTMLElement | SVGElement): Frame => {
    const f = entryFor(el)?.getFrame() ?? {
      translate: [0, 0] as [number, number],
      rotate: 0,
    };
    const frame: Frame = {
      translate: [f.translate[0], f.translate[1]],
      rotate: f.rotate,
    };
    if (el instanceof HTMLElement) framesRef.current.set(el, frame);
    return frame;
  };
  const frameFor = (el: HTMLElement | SVGElement): Frame =>
    (el instanceof HTMLElement && framesRef.current.get(el)) || seedFrame(el);
  const applyFrame = (el: HTMLElement | SVGElement, frame: Frame) => {
    (el as HTMLElement).style.transform =
      `translate(${frame.translate[0]}px, ${frame.translate[1]}px) rotate(${frame.rotate}deg)`;
  };

  // Group transforms are enabled only when every member supports them.
  const allConfigs = useMemo(
    () =>
      elements
        .map((el) => registry?.get(elementKey.get(el) ?? "")?.getConfig())
        .filter((c): c is NonNullable<typeof c> => !!c),
    [elements, elementKey, registry]
  );

  const guidelines = useMemo(() => {
    const vertical = new Set<number>([0, previewWidth / 2, previewWidth]);
    const horizontal = new Set<number>([0, previewHeight / 2, previewHeight]);
    const targets = collectSnapTargets(templateState, currentChapter.type);
    for (const target of targets) {
      if (target.id && selectedItemIds.includes(target.id)) continue;
      const left = target.position.x * previewWidth;
      const right = left + target.size.width * previewWidth;
      vertical
        .add(left)
        .add((left + right) / 2)
        .add(right);
      const top = target.position.y * previewHeight;
      const bottom = top + (target.size.height ?? 0) * previewHeight;
      horizontal
        .add(top)
        .add((top + bottom) / 2)
        .add(bottom);
    }
    return {
      vertical: [...vertical].map((v) => Math.round(v)),
      horizontal: [...horizontal].map((v) => Math.round(v)),
    };
  }, [
    templateState,
    currentChapter.type,
    selectedItemIds,
    previewWidth,
    previewHeight,
  ]);

  const bounds = useMemo(() => {
    // Cropped media: an axis-aligned bounds rect can't constrain a rotated mask,
    // so movement is clamped in onDrag instead (keeps the visible crop within
    // the canvas at any angle, letting the hidden parts spill outside).
    if (isMasked(singleConfig?.mask)) return undefined;
    // With position "css", right/bottom are insets from the container edges,
    // so 0/0 means "flush with the canvas" (the full preview area).
    const canvas = {
      left: 0,
      top: 0,
      right: 0,
      bottom: 0,
      position: "css" as const,
    };
    const boundsEl = singleConfig?.bounds;
    // Logo with an active header → constrain to the header; otherwise (no
    // header, or any other item) free within the canvas.
    if (boundsEl instanceof Element && rootRef.current) {
      const root = rootRef.current.getBoundingClientRect();
      const rect = boundsEl.getBoundingClientRect();
      return {
        left: Math.round(rect.left - root.left),
        top: Math.round(rect.top - root.top),
        right: Math.round(root.right - rect.right),
        bottom: Math.round(root.bottom - rect.bottom),
        position: "css" as const,
      };
    }
    return canvas;
  }, [singleConfig?.bounds, singleConfig?.mask, rootRef]);

  if (elements.length === 0) return null;

  const draggable = isGroup
    ? allConfigs.every((c) => c.draggable)
    : !!singleConfig?.draggable;
  const resizable = isGroup
    ? allConfigs.every((c) => c.resizable)
    : !!singleConfig?.resizable;
  const rotatable = isGroup
    ? allConfigs.length > 0 && allConfigs.every((c) => c.rotatable)
    : !!singleConfig?.rotatable;
  const keepRatio = isGroup ? false : !!singleConfig?.keepRatio;
  const renderDirections = isGroup
    ? GROUP_RESIZE_DIRECTIONS
    : (singleConfig?.resizeDirections ?? []);

  return (
    <>
      <DashedGuidelinesStyle />
      <Moveable
        ref={moveableRef}
        target={isGroup ? elements : elements[0]}
        rootContainer={rootRef.current ?? undefined}
        origin={false}
        draggable={draggable}
        resizable={resizable}
        rotatable={rotatable}
        keepRatio={keepRatio}
        renderDirections={renderDirections}
        throttleDrag={0}
        throttleResize={0}
        throttleRotate={0}
        snappable
        snapThreshold={SNAP_THRESHOLD}
        snapRotationDegrees={ROTATION_SNAP_DEGREES}
        snapRotationThreshold={ROTATION_SNAP_THRESHOLD}
        snapDirections={{
          top: true,
          left: true,
          bottom: true,
          right: true,
          center: true,
          middle: true,
        }}
        elementSnapDirections={{
          top: true,
          left: true,
          bottom: true,
          right: true,
          center: true,
          middle: true,
        }}
        verticalGuidelines={guidelines.vertical}
        horizontalGuidelines={guidelines.horizontal}
        bounds={bounds}
        // ── Single-target ──
        onDragStart={(e) => {
          setIsInteracting(true);
          e.set(seedFrame(e.target).translate);
        }}
        onDrag={(e) => {
          const f = frameFor(e.target);
          let tx = e.beforeTranslate[0] ?? 0;
          let ty = e.beforeTranslate[1] ?? 0;
          // Cropped media: keep the (rotated) crop within the canvas; the
          // hidden parts of the box may spill outside.
          const cropMask = singleConfig?.mask;
          if (isMasked(cropMask)) {
            const el = e.target as HTMLElement;
            [tx, ty] = clampMaskTranslate(
              tx,
              ty,
              cropMask,
              el.offsetWidth,
              el.offsetHeight,
              f.rotate,
              previewWidth,
              previewHeight
            );
          }
          f.translate = [tx, ty];
          applyFrame(e.target, f);
        }}
        onDragEnd={(e) => {
          setIsInteracting(false);
          const f = frameFor(e.target);
          singleEntry?.commitDrag(f.translate[0], f.translate[1]);
        }}
        onResizeStart={(e) => {
          setIsInteracting(true);
          const f = seedFrame(e.target);
          e.setOrigin(["%", "%"]);
          if (e.dragStart) e.dragStart.set(f.translate);
        }}
        onResize={(e) => {
          const f = frameFor(e.target);
          e.target.style.width = `${e.width}px`;
          if (!singleConfig?.autoHeight)
            e.target.style.height = `${e.height}px`;
          f.translate = [
            e.drag.beforeTranslate[0] ?? 0,
            e.drag.beforeTranslate[1] ?? 0,
          ];
          applyFrame(e.target, f);
          // The media element carries a fixed pixel size (for stroke offset), so
          // it wouldn't follow the box during the gesture. Resize it live to the
          // new box so object-fit:cover re-fits (cover, centered) on every frame,
          // matching the committed result instead of snapping on drag end.
          const media = e.target.querySelector<HTMLElement>("img, video");
          if (media) {
            media.style.width = `${e.width}px`;
            media.style.height = `${e.height}px`;
          }
        }}
        onResizeEnd={(e) => {
          setIsInteracting(false);
          const f = frameFor(e.target);
          const last = e.lastEvent;
          if (last)
            singleEntry?.commitResize({
              leftPx: f.translate[0],
              topPx: f.translate[1],
              widthPx: last.width,
              heightPx: last.height,
            });
        }}
        onRotateStart={(e) => {
          setIsInteracting(true);
          e.set(seedFrame(e.target).rotate);
        }}
        onRotate={(e) => {
          const f = frameFor(e.target);
          f.rotate = e.beforeRotate;
          applyFrame(e.target, f);
        }}
        onRotateEnd={(e) => {
          setIsInteracting(false);
          const f = frameFor(e.target);
          singleEntry?.commitRotate(f.rotate);
        }}
        // ── Group ──
        onDragGroupStart={(e) => {
          setIsInteracting(true);
          e.events.forEach((ev) => ev.set(seedFrame(ev.target).translate));
        }}
        onDragGroup={(e) => {
          e.events.forEach((ev) => {
            const f = frameFor(ev.target);
            f.translate = [
              ev.beforeTranslate[0] ?? 0,
              ev.beforeTranslate[1] ?? 0,
            ];
            applyFrame(ev.target, f);
          });
        }}
        onDragGroupEnd={(e) => {
          setIsInteracting(false);
          e.events.forEach((ev) => {
            const f = frameFor(ev.target);
            entryFor(ev.target)?.commitDrag(f.translate[0], f.translate[1]);
          });
        }}
        onResizeGroupStart={(e) => {
          setIsInteracting(true);
          e.events.forEach((ev) => {
            const f = seedFrame(ev.target);
            ev.setOrigin(["%", "%"]);
            if (ev.dragStart) ev.dragStart.set(f.translate);
          });
        }}
        onResizeGroup={(e) => {
          e.events.forEach((ev) => {
            const f = frameFor(ev.target);
            ev.target.style.width = `${ev.width}px`;
            ev.target.style.height = `${ev.height}px`;
            f.translate = [
              ev.drag.beforeTranslate[0] ?? 0,
              ev.drag.beforeTranslate[1] ?? 0,
            ];
            applyFrame(ev.target, f);
          });
        }}
        onResizeGroupEnd={(e) => {
          setIsInteracting(false);
          e.events.forEach((ev) => {
            const f = frameFor(ev.target);
            const last = ev.lastEvent;
            if (last)
              entryFor(ev.target)?.commitResize({
                leftPx: f.translate[0],
                topPx: f.translate[1],
                widthPx: last.width,
                heightPx: last.height,
              });
          });
        }}
        onRotateGroupStart={(e) => {
          setIsInteracting(true);
          e.events.forEach((ev) => {
            const f = seedFrame(ev.target);
            ev.set(f.rotate);
            if (ev.dragStart) ev.dragStart.set(f.translate);
          });
        }}
        onRotateGroup={(e) => {
          e.events.forEach((ev) => {
            const f = frameFor(ev.target);
            f.rotate = ev.beforeRotate;
            f.translate = [
              ev.drag.beforeTranslate[0] ?? 0,
              ev.drag.beforeTranslate[1] ?? 0,
            ];
            applyFrame(ev.target, f);
          });
        }}
        onRotateGroupEnd={(e) => {
          setIsInteracting(false);
          e.events.forEach((ev) => {
            const f = frameFor(ev.target);
            const entry = entryFor(ev.target);
            // Group rotation moves members around the group center, so commit
            // both the new position and the new angle.
            entry?.commitDrag(f.translate[0], f.translate[1]);
            entry?.commitRotate(f.rotate);
          });
        }}
      />
    </>
  );
};

// ─── Styled components ───

// Render the snap guidelines as dashed lines (Moveable draws them solid by
// default). Mirrors Moveable's own `.line.dashed` rule.
const DashedGuidelinesStyle = createGlobalStyle`
  .moveable-line.moveable-guideline {
    background: transparent !important;
  }
  .moveable-line.moveable-guideline.moveable-horizontal {
    border-top: 1px dashed var(--moveable-color) !important;
  }
  .moveable-line.moveable-guideline.moveable-vertical {
    border-left: 1px dashed var(--moveable-color) !important;
  }
`;
